import React from 'react'
import { XStack, YStack } from 'tamagui'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import { CobuSimpleAvatar, CobuStack, CobuText } from '@cobu/ui'
import type { PlaceComment } from '@cobu/types'
import { useDeletePlaceCommentMutation, useReactToPlaceCommentMutation } from '@cobu/api-client'
import { formatRelativeTime } from '../../utils/dateTime'
import { Strings } from '../../constants/Strings'

// Place comments show a single heart (PRD §3.2), not the Feed's full emoji
// picker. The backend is generic on reaction name; we always send this one.
const HEART_REACTION = 'love'

interface PlaceNeighborChatProps {
  placeId: string
  comments: PlaceComment[]
  /** Current user's uuid — controls which comments show a Delete affordance. */
  currentUserId?: string
}

/**
 * Flat neighbor-chat renderer for a place's comments. There is no threading —
 * the PRD keeps replies on the Feed, so place comments are a single flat list.
 * The compose FAB lives on the detail screen (outside the ScrollView) so it
 * anchors to the viewport like the app's other floating buttons; the trailing
 * padding here keeps the last comment clear of it. A comment shows a Delete
 * action only to its own author (the backend also allows admins, but the
 * resident app doesn't surface that here).
 */
export function PlaceNeighborChat({ placeId, comments, currentUserId }: PlaceNeighborChatProps) {
  const deleteComment = useDeletePlaceCommentMutation(placeId)
  const reactToComment = useReactToPlaceCommentMutation(placeId)
  const strings = Strings.get().places.chat

  return (
    <YStack gap="$1" pb={96}>
      <CobuText variant="label" color="$colorSecondary" px="$4" pt="$3" textTransform="uppercase" fontSize="$1">
        {strings.heading}
      </CobuText>

      <YStack gap="$3" px="$4" py="$2">
        {comments.length === 0 ? (
          <CobuText variant="body" color="$colorSecondary" py="$2">
            {strings.empty}
          </CobuText>
        ) : (
          comments.map((comment) => (
            <CommentRow
              key={comment.uuid}
              comment={comment}
              canDelete={!!currentUserId && comment.user === currentUserId}
              onDelete={() => deleteComment.mutate({ commentId: comment.uuid })}
              onToggleReaction={() =>
                reactToComment.mutate({
                  commentId: comment.uuid,
                  reactionName: HEART_REACTION,
                  hasReacted: !!comment.myReaction,
                })
              }
            />
          ))
        )}
      </YStack>
    </YStack>
  )
}

function CommentRow({
  comment,
  canDelete,
  onDelete,
  onToggleReaction,
}: {
  comment: PlaceComment
  canDelete: boolean
  onDelete: () => void
  onToggleReaction: () => void
}) {
  const reacted = !!comment.myReaction
  return (
    <XStack gap="$2" ai="flex-start">
      <CobuSimpleAvatar
        user={{ uuid: comment.user, displayName: comment.authorName }}
        size={28}
        fontSize={10}
      />
      <CobuStack f={1} bg="$backgroundSecondary" br="$3" p="$3" gap="$1">
        <CobuText variant="body" fow="600" fontSize="$2">
          {comment.authorName}
        </CobuText>
        <CobuText variant="body" fontSize="$2">{comment.contents}</CobuText>
        <XStack ai="center" gap="$3" mt="$1">
          <CobuText variant="caption" color="$colorSecondary">{formatRelativeTime(comment.createdAt)}</CobuText>

          <XStack ai="center" gap="$1" onPress={onToggleReaction} style={{ cursor: 'pointer' }}>
            {reacted
              ? <FavoriteIcon style={{ fontSize: 14, color: '#EF4A4A' }} />
              : <FavoriteBorderIcon style={{ fontSize: 14 }} />}
            {comment.reactionsCount > 0 && (
              <CobuText variant="caption" color="$colorSecondary">{comment.reactionsCount}</CobuText>
            )}
          </XStack>

          {canDelete && (
            <CobuText variant="caption" color="$red" fow="600" onPress={onDelete}>
              {Strings.get().places.chat.delete}
            </CobuText>
          )}
        </XStack>
      </CobuStack>
    </XStack>
  )
}
